import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { CompanionSettings } from '@/components/CompanionSettings'
import { Kicker } from '@/components/Kicker'
import { ListRow } from '@/components/ListRow'
import { Button } from '@/components/Button'
import { useAppStore } from '@/stores/appStore'
import { db } from '@/lib/db'
import { stableDeviceId } from '@/lib/device'
import { PACK_COLORS } from '@/design-system/tokens'
import { drainOutbox, catchUp } from '@/lib/sync'
import { supabase } from '@/lib/supabase'
import { exportDiapersCsv, exportFeedsCsv, exportGrowthCsv, exportSleepCsv } from '@/lib/csvExport'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'

export function SettingsScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const familyRole = useAppStore((s) => s.familyRole)
  const persons = useAppStore((s) => s.persons)
  const online = useAppStore((s) => s.online)
  const signOut = useAppStore((s) => s.signOut)

  const [deviceId, setDeviceId] = useState<string>('—')
  const [outbox, setOutbox] = useState({ pending: 0, in_flight: 0, failed: 0 })
  const [syncing, setSyncing] = useState(false)
  const [failedQueue, setFailedQueue] = useState<{ id: string; attempts: number; retry: string }[]>([])
  useEffect(() => {
    let active = true
    const refresh = async () => {
      const failures = await db.outbox.where('state').equals('failed').toArray()
      const events = await db.events.bulkGet(failures.map((f) => f.event_id))
      if (active) setFailedQueue(failures.flatMap((f, i) =>
        events[i]?.family_id === familyId && events[i]?.user_id === user?.id
          ? [{ id: f.event_id, attempts: f.attempts, retry: f.next_retry_at }] : []))
    }
    void refresh()
    const timer = setInterval(() => { void refresh() }, 3000)
    return () => { active = false; clearInterval(timer) }
  }, [familyId, user?.id])

  useEffect(() => {
    void stableDeviceId().then(setDeviceId)
  }, [])

  useEffect(() => {
    const refresh = async () => {
      const [pending, inFlight, failed] = await Promise.all([
        db.outbox.where('state').equals('pending').count(),
        db.outbox.where('state').equals('in_flight').count(),
        db.outbox.where('state').equals('failed').count(),
      ])
      setOutbox({ pending, in_flight: inFlight, failed })
    }
    void refresh()
    const t = setInterval(refresh, 3000)
    return () => clearInterval(t)
  }, [])

  async function forceSync() {
    if (!familyId) return
    setSyncing(true)
    try {
      await drainOutbox()
      await catchUp(familyId)
    } finally {
      setSyncing(false)
    }
  }

  async function retryFailed() {
    const now = new Date().toISOString()
    await db.transaction('rw', db.outbox, db.events, async () => {
      const entries = await db.outbox.where('state').equals('failed').toArray()
      for (const entry of entries) {
        const event = await db.events.get(entry.event_id)
        if (event?.family_id === familyId && event?.user_id === user?.id) {
          await db.outbox.update(entry.event_id, { state: 'pending', next_retry_at: now, attempts: 0, last_error: null })
        }
      }
    })
    await forceSync()
  }

  const focusedPerson = useFocusedPerson()
  const [csvBusy, setCsvBusy] = useState<string | null>(null)
  async function csvExport(kind: 'feeds' | 'diapers' | 'growth' | 'sleep', days: number) {
    if (!familyId) return
    setCsvBusy(kind)
    try {
      const personId = focusedPerson?.id ?? null
      if (kind === 'feeds') await exportFeedsCsv(familyId, personId, days)
      if (kind === 'diapers') await exportDiapersCsv(familyId, personId, days)
      if (kind === 'growth') await exportGrowthCsv(familyId, personId, days)
      if (kind === 'sleep') await exportSleepCsv(familyId, personId, days)
    } catch (e) {
      window.alert(`Export failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setCsvBusy(null)
    }
  }

  const [exporting, setExporting] = useState(false)
  async function exportData() {
    if (!familyId) return
    setExporting(true)
    try {
      const { downloadFamilyArchive } = await import('@/lib/exportArchive')
      const archive = await downloadFamilyArchive(familyId)
      if (archive.manifest.status !== 'complete') {
        window.alert(`Partial archive saved — not a complete backup (${archive.manifest.error?.code ?? 'incomplete'}). Keep the file for inspection/retry.`)
      }
    } catch (e) {
      window.alert(`Export failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setExporting(false)
    }
  }

  const [deleting, setDeleting] = useState(false)
  async function deleteAccount() {
    const confirmA = window.prompt(
      `⚠️  This permanently deletes your account and every family where you're the sole member.\n\nAny family you share with others: your membership is revoked, but their data stays.\n\nType exactly:  DELETE MY ACCOUNT`,
    )
    if (confirmA !== 'DELETE MY ACCOUNT') {
      if (confirmA !== null) window.alert('Did not match. Nothing was deleted.')
      return
    }
    setDeleting(true)
    try {
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { confirm: 'DELETE MY ACCOUNT' },
      })
      if (error) {
        let detail = error.message
        try {
          const anyErr = error as { context?: { text?: () => Promise<string> } }
          if (anyErr.context?.text) {
            const body = await anyErr.context.text()
            const parsed = JSON.parse(body)
            detail = parsed.error === 'sole_owner_of_shared_family'
              ? 'You are the only owner of a shared family. Transfer ownership or remove other members first.'
              : parsed.detail ?? parsed.error ?? body
          }
        } catch { /* keep original */ }
        throw new Error(detail)
      }
      window.alert(`Account deleted. Bye.\n\nDeleted ${JSON.stringify(data, null, 2)}`)
      await signOut()
    } catch (e) {
      window.alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Screen title="Settings" back>
      <div className="px-4 py-3 space-y-6">
        <CompanionSettings />
        <section>
          <Kicker>Account</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            <ListRow title="Signed in as" subtitle={user?.email ?? '—'} />
            <ListRow title="Family role" subtitle={familyRole ?? '—'} />
            <ListRow title="Family ID" subtitle={familyId ?? '—'} />
            <ListRow title="Device ID" subtitle={deviceId} />
          </div>
        </section>

        <section>
          <Kicker>People</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            <ListRow title="Persons on graph" subtitle={String(persons.length)} onClick={() => nav('/family')} />
            <ListRow title="Manage caregivers & persons" onClick={() => nav('/family')} />
          </div>
        </section>

        <section>
          <Kicker>Records</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            <ListRow title="Documents" subtitle="Insurance, immunization, prescriptions" onClick={() => nav('/documents')} />
          </div>
        </section>

        <section>
          <Kicker>Sync</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            <ListRow title="Network" subtitle={online ? 'Online' : 'Offline — entries queue locally'} />
            <ListRow title="Pending events" subtitle={String(outbox.pending)} />
            <ListRow title="In flight" subtitle={String(outbox.in_flight)} />
            <ListRow title="Failed — not confirmed on server" subtitle={`${outbox.failed}. Automatic retry continues hourly; Retry failed keeps the original event IDs.`} />
            {failedQueue.map((entry) => (
              <ListRow key={entry.id} title={`Event ${entry.id}`} subtitle={`${entry.attempts} attempts · next retry ${entry.retry}`} />
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" size="md" loading={syncing} onClick={forceSync} className="flex-1">
              Sync now
            </Button>
            {outbox.failed > 0 && (
              <Button variant="secondary" size="md" onClick={retryFailed} className="flex-1">
                Retry failed
              </Button>
            )}
          </div>
        </section>

        <section>
          <Kicker>Pediatric weekly report (PDF)</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4 space-y-3">
            <div className="text-caption text-text-secondary">
              A one-page printable summary for {focusedPerson?.display_name ?? 'the focused person'}'s next
              pediatric visit. Feeds, diapers, sleep, medications, growth, notes — totals + day-by-day.
              Save as PDF from your browser's print dialog.
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="md" onClick={() => nav('/reports/weekly?days=7')} className="flex-1">
                Last 7 days
              </Button>
              <Button variant="secondary" size="md" onClick={() => nav('/reports/weekly?days=14')} className="flex-1">
                Last 14 days
              </Button>
              <Button variant="secondary" size="md" onClick={() => nav('/reports/weekly?days=30')} className="flex-1">
                Last 30 days
              </Button>
            </div>
          </div>
        </section>

        <section>
          <Kicker>Pediatric-visit exports (CSV)</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4 space-y-3">
            <div className="text-caption text-text-secondary">
              Plain columns your pediatrician can open in any spreadsheet.
              {focusedPerson && <> Scoped to <span className="text-text-primary font-medium">{focusedPerson.display_name}</span>.</>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" size="md" loading={csvBusy === 'feeds'} onClick={() => csvExport('feeds', 30)}>
                Feeds (30d)
              </Button>
              <Button variant="secondary" size="md" loading={csvBusy === 'diapers'} onClick={() => csvExport('diapers', 30)}>
                Diapers (30d)
              </Button>
              <Button variant="secondary" size="md" loading={csvBusy === 'sleep'} onClick={() => csvExport('sleep', 30)}>
                Sleep (30d)
              </Button>
              <Button variant="secondary" size="md" loading={csvBusy === 'growth'} onClick={() => csvExport('growth', 365)}>
                Growth (1y)
              </Button>
            </div>
          </div>
        </section>

        <section>
          <Kicker>Privacy</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4 text-caption text-text-secondary space-y-3">
            <p>
              Wren only ever emails you (the operator). Caregivers you invite receive links that you share personally
              — never a system-sent email. See the medication logger for the strict-confirmation flow.
            </p>
            <div>
              <Button variant="secondary" size="md" loading={exporting} onClick={exportData} className="w-full">
                Download family archive (offline HTML)
              </Button>
              <div className="mt-1 text-caption text-text-quiet">
                The selected family's record, with verified available media, in a private file you can read offline.
                Missing files or interrupted pages are marked partial. This is not a live-access grant or database backup.
              </div>
            </div>
          </div>
        </section>

        <section>
          <Kicker color={PACK_COLORS.safety.light}>Danger zone</Kicker>
          <div className="mt-3 space-y-2">
            <Button variant="secondary" size="lg" onClick={signOut} className="w-full">
              Sign out
            </Button>
            <Button variant="destructive" size="lg" loading={deleting} onClick={deleteAccount} className="w-full">
              Delete my account permanently
            </Button>
            <div className="text-caption text-text-quiet px-1">
              Delete removes your auth account + wipes families where you're the sole member. Shared families stay intact
              without you. This can't be undone.
            </div>
          </div>
        </section>

        <div className="pt-6 pb-8 text-center text-caption text-text-quiet">
          <div className="font-serif italic text-lg">Wren</div>
          <div className="mt-1">A Person Graph for your family. Yours forever.</div>
        </div>
      </div>
    </Screen>
  )
}
